import { useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import MetricCard from "../components/MetricCard";
import { getStatsSms } from "../api/adminApi";
import type { StatsSmsResponse } from "../types/admin";
import { formatDateTime, TIMEZONE_LABEL } from "../utils/timezone";

type TimeRange = 24 | 48 | 72 | 168;

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: 24, label: "24 Hours" },
  { value: 48, label: "48 Hours" },
  { value: 72, label: "72 Hours" },
  { value: 168, label: "7 Days" },
];

/**
 * Delivery Rate Gauge Component
 * Visual indicator showing SMS delivery success rate
 */
function DeliveryRateGauge({ rate }: { rate: number }) {
  // Determine color based on rate
  const getColor = (r: number) => {
    if (r >= 98) return "var(--accent-success)";
    if (r >= 95) return "var(--accent-warning)";
    return "var(--accent-danger)";
  };

  const color = getColor(rate);
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (rate / 100) * circumference;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg width="140" height="140" style={{ transform: "rotate(-90deg)" }}>
        {/* Background circle */}
        <circle
          cx="70"
          cy="70"
          r="45"
          fill="none"
          stroke="#374151"
          strokeWidth="12"
        />
        {/* Progress circle */}
        <circle
          cx="70"
          cy="70"
          r="45"
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: "stroke-dashoffset 0.5s ease" }}
        />
      </svg>
      <div
        style={{
          position: "relative",
          marginTop: "-90px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: "1.75rem",
            fontWeight: "bold",
            color,
          }}
        >
          {rate.toFixed(1)}%
        </span>
        <span style={{ fontSize: "0.875rem", color: "#94a3b8" }}>Delivery Rate</span>
      </div>
    </div>
  );
}

function SMSMonitoring() {
  const [data, setData] = useState<StatsSmsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>(24);

  const loadData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const result = await getStatsSms(timeRange);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load SMS data");
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTimeRangeChange = (newRange: TimeRange) => {
    setTimeRange(newRange);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={loadData} style={{ marginTop: "1rem" }}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { stats, daily, period } = data;
  const chartData = [...daily].reverse();

  // Calculate totals for chart
  const totalAlerts = daily.reduce((sum, d) => sum + d.alertsSent, 0);
  const totalInvites = daily.reduce((sum, d) => sum + d.invitesSent, 0);

  return (
    <div>
      <header className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 className="page-title">SMS Monitoring</h1>
            <p className="page-subtitle">
              Last {period.hours} hours: {formatDateTime(period.start)} to {formatDateTime(period.end)} {TIMEZONE_LABEL}
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {TIME_RANGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleTimeRangeChange(option.value)}
                style={{
                  padding: "0.5rem 1rem",
                  borderRadius: "0.5rem",
                  border: "none",
                  cursor: "pointer",
                  backgroundColor:
                    timeRange === option.value ? "var(--accent-primary)" : "#374151",
                  color: timeRange === option.value ? "white" : "#94a3b8",
                  fontWeight: timeRange === option.value ? 600 : 400,
                  transition: "all 0.2s ease",
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Delivery Rate and Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "1.5rem" }}>
        {/* Gauge Card */}
        <div className="card">
          <div className="card-body" style={{ display: "flex", justifyContent: "center", paddingTop: "2rem" }}>
            <DeliveryRateGauge rate={stats.deliveryRate} />
          </div>
        </div>

        {/* Summary Metrics */}
        <div className="metrics-grid">
          <MetricCard label="Total Sent" value={stats.sent} />
          <MetricCard
            label="Delivered"
            value={stats.delivered}
            emphasis={stats.delivered > 0 ? "success" : "default"}
          />
          <MetricCard
            label="Failed"
            value={stats.failed}
            emphasis={stats.failed > 0 ? "danger" : "default"}
          />
          <MetricCard
            label="Queued"
            value={stats.queued}
            emphasis={stats.queued > 10 ? "warning" : "default"}
          />
        </div>
      </div>

      {/* SMS by Type Summary */}
      <div className="metrics-grid" style={{ marginTop: "1.5rem", gridTemplateColumns: "1fr 1fr" }}>
        <MetricCard
          label="Alert SMS (Safety Alerts)"
          value={totalAlerts}
          emphasis="danger"
        />
        <MetricCard
          label="Invite SMS (Circle Invites)"
          value={totalInvites}
          emphasis="primary"
        />
      </div>

      {/* Daily SMS Chart */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">SMS Volume by Day</h3>
        </div>
        <div className="card-body">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                  tickFormatter={(value) => value?.slice(5) || ""}
                />
                <YAxis
                  stroke="#94a3b8"
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #475569",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#f1f5f9" }}
                />
                <Legend />
                <Bar
                  dataKey="alertsSent"
                  name="Alert SMS"
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="invitesSent"
                  name="Invite SMS"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Daily Breakdown Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Daily Breakdown</h3>
        </div>
        <div className="card-body">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Alert SMS</th>
                <th>Invite SMS</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {daily.map((day) => (
                <tr key={day.date}>
                  <td>{day.date}</td>
                  <td style={{ color: day.alertsSent > 0 ? "var(--accent-danger)" : "inherit" }}>
                    {day.alertsSent}
                  </td>
                  <td style={{ color: day.invitesSent > 0 ? "var(--accent-primary)" : "inherit" }}>
                    {day.invitesSent}
                  </td>
                  <td style={{ fontWeight: 600 }}>{day.alertsSent + day.invitesSent}</td>
                </tr>
              ))}
              {daily.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", color: "#64748b" }}>
                    No SMS activity in selected period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status Indicators */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Delivery Health</h3>
        </div>
        <div className="card-body">
          <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
            <StatusIndicator
              label="Delivery Rate"
              status={stats.deliveryRate >= 98 ? "healthy" : stats.deliveryRate >= 95 ? "warning" : "critical"}
              value={`${stats.deliveryRate.toFixed(1)}%`}
            />
            <StatusIndicator
              label="Queue Backlog"
              status={stats.queued === 0 ? "healthy" : stats.queued <= 10 ? "warning" : "critical"}
              value={`${stats.queued} pending`}
            />
            <StatusIndicator
              label="Failure Rate"
              status={stats.sent === 0 ? "healthy" : (stats.failed / stats.sent) < 0.02 ? "healthy" : (stats.failed / stats.sent) < 0.05 ? "warning" : "critical"}
              value={stats.sent === 0 ? "0%" : `${((stats.failed / stats.sent) * 100).toFixed(1)}%`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Status Indicator Component
 */
function StatusIndicator({
  label,
  status,
  value,
}: {
  label: string;
  status: "healthy" | "warning" | "critical";
  value: string;
}) {
  const statusConfig = {
    healthy: { color: "var(--accent-success)", icon: "✓", bg: "rgba(34, 197, 94, 0.1)" },
    warning: { color: "var(--accent-warning)", icon: "⚠", bg: "rgba(234, 179, 8, 0.1)" },
    critical: { color: "var(--accent-danger)", icon: "✕", bg: "rgba(239, 68, 68, 0.1)" },
  };

  const config = statusConfig[status];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.75rem 1rem",
        backgroundColor: config.bg,
        borderRadius: "0.5rem",
        border: `1px solid ${config.color}30`,
      }}
    >
      <span
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: config.color,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.75rem",
          fontWeight: "bold",
        }}
      >
        {config.icon}
      </span>
      <div>
        <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{label}</div>
        <div style={{ fontSize: "1rem", fontWeight: 600, color: config.color }}>{value}</div>
      </div>
    </div>
  );
}

export default SMSMonitoring;
